import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import qs from 'qs'
import * as R from 'ramda'

import { fetchQuizDiagnostics } from 'services/QuizDiagnosticsService'

import { QuizDiagnosticsBookGroup } from '../types'
import { QuizDiagnosticsApiResponse } from '../types/api'
import {
  buildChapterFilterOptions,
  getSafeChapterFilterValue
} from '../utils/filterQuizDiagnosticsData'
import {
  filterBookGroupsByChapterId,
  flattenBookGroupChapters,
  getFilteredOverallPercent,
  mapQuizDiagnosticsApiResponse
} from '../utils/mapQuizDiagnosticsApiResponse'
import { QUIZ_DIAGNOSTICS_PARAMS } from '../utils/quizDiagnosticsParams'
import {
  getSharedSrqStatus,
  patchSharedSrqStatus
} from '../utils/srqStatusStore'

const ALL_CHAPTERS_VALUE = 'all'

const EMPTY_MAPPED_STATE = {
  srqEnabled: false,
  srqRedDotsVisible: true,
  bookGroups: [] as QuizDiagnosticsBookGroup[],
  overallPercent: 0,
  totalQuizzes: 0,
  hasData: false
}

export const useQuizDiagnosticsData = () => {
  const {
    location: { search }
  } = useHistory()

  const [mappedData, setMappedData] = useState(EMPTY_MAPPED_STATE)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true })
  const selectedChapterId = R.pathOr(
    ALL_CHAPTERS_VALUE,
    [QUIZ_DIAGNOSTICS_PARAMS.chapterId],
    parsedQuery
  )

  const handleLoadDiagnostics = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) {
        setIsLoading(true)
      }

      setHasError(false)

      try {
        const response = await fetchQuizDiagnostics()
        const responseData = response?.data as
          | QuizDiagnosticsApiResponse
          | undefined
        const mapped = mapQuizDiagnosticsApiResponse(responseData)

        if (
          mapped.srqEnabled &&
          mapped.srqRedDotsVisible !== getSharedSrqStatus().srqRedDotsVisible
        ) {
          patchSharedSrqStatus({
            srqRedDotsVisible: mapped.srqRedDotsVisible
          })
        }

        setMappedData(mapped)
      } catch (error) {
        console.error('Quiz diagnostics API unavailable:', error)
        setMappedData(EMPTY_MAPPED_STATE)
        setHasError(true)
      } finally {
        if (!silent) {
          setIsLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    handleLoadDiagnostics()
  }, [handleLoadDiagnostics])

  const previousSelectedChapterIdRef = useRef<string | null>(null)

  const allChapters = useMemo(
    () => flattenBookGroupChapters(mappedData.bookGroups),
    [mappedData.bookGroups]
  )

  const chapterFilterOptions = useMemo(
    () => buildChapterFilterOptions(allChapters),
    [allChapters]
  )

  const safeSelectedChapterId = useMemo(() => {
    const safeValue = getSafeChapterFilterValue(
      selectedChapterId,
      chapterFilterOptions
    )

    return safeValue?.value ?? ALL_CHAPTERS_VALUE
  }, [chapterFilterOptions, selectedChapterId])

  useEffect(() => {
    const previousSelectedChapterId = previousSelectedChapterIdRef.current

    if (
      previousSelectedChapterId !== null &&
      previousSelectedChapterId !== ALL_CHAPTERS_VALUE &&
      safeSelectedChapterId === ALL_CHAPTERS_VALUE
    ) {
      handleLoadDiagnostics({ silent: true })
    }

    previousSelectedChapterIdRef.current = safeSelectedChapterId
  }, [handleLoadDiagnostics, safeSelectedChapterId])

  const bookGroups = useMemo(
    () =>
      filterBookGroupsByChapterId(mappedData.bookGroups, safeSelectedChapterId),
    [mappedData.bookGroups, safeSelectedChapterId]
  )

  const summary = useMemo(
    () => ({
      overallPercent: getFilteredOverallPercent(
        mappedData.bookGroups,
        safeSelectedChapterId,
        mappedData.overallPercent
      ),
      totalQuizzes: mappedData.totalQuizzes ?? 0,
      isEmpty:
        !mappedData.hasData ||
        flattenBookGroupChapters(bookGroups).length === 0
    }),
    [bookGroups, mappedData, safeSelectedChapterId]
  )

  return {
    bookGroups,
    summary,
    isLoading,
    hasError,
    srqEnabled: mappedData.srqEnabled,
    selectedChapterId: safeSelectedChapterId,
    chapterFilterOptions,
    refetch: () => handleLoadDiagnostics({ silent: true })
  }
}
